import React, { useEffect, useMemo, useState } from "react";
import { apiCall } from "../../utils/apiService";
import { getApiDisplayBase } from "../../utils/apiDisplayBase";
import { buildSendGroupApiExamples } from "./messageApiExampleBuilders";
import MessageApiExamplesPanel from "./MessageApiExamplesPanel";
import MessageHeaderLayer from "./MessageHeaderLayer";

const GroupLayer = () => {
  const [alertMessage, setAlertMessage] = useState(null);
  const [spinner, setSpinner] = useState(false);
  const [groups, setGroups] = useState([]);
  const [data, setData] = useState({
    client_id: "",
    typedMsg: "",
    selectedGroupId: "",
    manualGroupId: "",
    manualGroupName: "",
  });

  useEffect(() => {
    document.title = "Send group";
  }, []);

  useEffect(() => {
    const id = data.client_id;
    if (!id) {
      setGroups([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiCall({
          url: `groups?id=${encodeURIComponent(id)}`,
          method: "GET",
        });
        const raw = res.data;
        const arr = Array.isArray(raw) ? raw : [];
        const onlyGroups = arr.filter((g) => g && g.id && String(g.id).includes("@g.us"));
        if (!cancelled) setGroups(onlyGroups);
      } catch {
        if (!cancelled) setGroups([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data.client_id]);

  const selectedGroup = useMemo(
    () => groups.find((g) => String(g.id) === String(data.selectedGroupId)),
    [groups, data.selectedGroupId]
  );

  const resolvedGroupData = () => {
    if (selectedGroup) {
      return {
        id: selectedGroup.id,
        name: selectedGroup.name || selectedGroup.subject || selectedGroup.notify || "Group",
      };
    }
    const id = String(data.manualGroupId || "").trim();
    const name = String(data.manualGroupName || "").trim() || "Group";
    if (!id) return null;
    return { id, name };
  };

  const validated = () => {
    if (!data.client_id) {
      return { isValid: false, messageText: "Instance is required!" };
    }
    const gd = resolvedGroupData();
    if (!gd) {
      return { isValid: false, messageText: "Select a group or enter a group JID (@g.us)." };
    }
    if (!String(data.typedMsg || "").trim()) {
      return { isValid: false, messageText: "Message is required!" };
    }
    return { isValid: true, messageText: "" };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { isValid, messageText } = validated();
    if (!isValid) {
      setAlertMessage({ message: messageText, class: "danger" });
      hideAlert(2);
      return;
    }
    const gd = resolvedGroupData();
    setSpinner(true);
    try {
      const response = await apiCall({
        url: "send-message/send-to-group",
        method: "POST",
        body: {
          type: "type",
          typedMsg: String(data.typedMsg).trim(),
          client_id: data.client_id,
          groupData: gd,
        },
      });
      setAlertMessage({
        message: response.msg || response.message || "Done",
        class: response.success ? "success" : "danger",
      });
      if (response.success) {
        setData((prev) => ({ ...prev, typedMsg: "" }));
      }
    } catch (error) {
      setAlertMessage({ message: error.message || "Could not send.", class: "danger" });
    } finally {
      setSpinner(false);
      hideAlert(3);
    }
  };

  const hideAlert = (sec = 1) => {
    setTimeout(() => {
      setAlertMessage(null);
      setSpinner(false);
    }, sec * 1000);
  };

  const apiBase = getApiDisplayBase();
  const codeExamples = useMemo(() => buildSendGroupApiExamples(apiBase), [apiBase]);

  return (
    <>
      <div className="card">
        <div className="card-body">
          <MessageHeaderLayer
            getHeaderData={(id) => setData((prev) => ({ ...prev, client_id: id || "" }))}
          />
          <div className="row g-0">
            <div className="col-md-6 mt-3 pe-md-3">
              <form className="border rounded" onSubmit={handleSubmit}>
                <div
                  className="p-3 text-white"
                  style={{
                    borderTopLeftRadius: "5px",
                    borderTopRightRadius: "5px",
                    background: "#26a69a",
                  }}
                >
                  Request test
                </div>
                <div className="row m-0 g-0 p-3">
                  <div className="alert alert-success" style={{ fontSize: "12px" }}>
                    Sends to a WhatsApp group. Use @tagall in the message to mention everyone (server
                    expands mentions).
                  </div>
                  {alertMessage && (
                    <div className={`alert alert-${alertMessage.class}`} style={{ fontSize: "12px" }}>
                      {alertMessage.message}
                    </div>
                  )}
                  <label className="form-label text-secondary-light text-sm mb-1">Group</label>
                  <select
                    className="form-select bg-white mb-2"
                    value={data.selectedGroupId}
                    onChange={(e) =>
                      setData((prev) => ({ ...prev, selectedGroupId: e.target.value }))
                    }
                    disabled={!data.client_id || groups.length === 0}
                  >
                    <option value="">
                      {groups.length === 0 && data.client_id
                        ? "No groups in list — use manual JID below"
                        : "Select a group"}
                    </option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name || g.subject || g.notify || g.id}
                      </option>
                    ))}
                  </select>
                  <span className="text-xs text-secondary-light mb-2 d-block">
                    List comes from <code className="text-xs">/api/groups?id=…</code> (synced groups
                    file). Or enter JID manually.
                  </span>
                  <input
                    type="text"
                    className="form-control mb-1"
                    placeholder="Manual group JID e.g. 120363…@g.us"
                    value={data.manualGroupId}
                    onChange={(e) =>
                      setData((prev) => ({ ...prev, manualGroupId: e.target.value }))
                    }
                  />
                  <input
                    type="text"
                    className="form-control mb-3"
                    placeholder="Group display name (optional)"
                    value={data.manualGroupName}
                    onChange={(e) =>
                      setData((prev) => ({ ...prev, manualGroupName: e.target.value }))
                    }
                  />
                  <textarea
                    rows={5}
                    className="form-control"
                    placeholder="Type message here…"
                    value={data.typedMsg}
                    onChange={(e) => setData((prev) => ({ ...prev, typedMsg: e.target.value }))}
                  />
                  <div className="d-flex justify-content-center mt-3">
                    <button
                      type="submit"
                      className="btn btn-success"
                      style={{ background: "#26a69a" }}
                      disabled={spinner}
                    >
                      {spinner ? "Sending…" : "Send to group"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
            <div className="col-md-6 mt-3 ps-md-3">
              <MessageApiExamplesPanel examples={codeExamples} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GroupLayer;
